import { cn } from "@sylviq/ui-react-web/lib/cn";
import { ChevronDown, Loader2, RotateCw } from "lucide-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Optional heading that groups consecutive options (e.g. an account type). */
  group?: string;
  /** Extra searchable text (code, number, contact) beyond the visible label. */
  keywords?: string;
  disabled?: boolean;
}

export interface SearchSelectProps {
  /** Accessible name for a picker that has no wrapping `<label>`. */
  "aria-label"?: string;
  /** Id applied to the trigger so a wrapping `<label htmlFor>` names it. */
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string;
  /** Search field placeholder while the panel is open. */
  searchPlaceholder?: string;
  /** Shown when the query matches no option. */
  emptyMessage?: string;
  /** Replaces the option list with a busy state. */
  loading?: boolean;
  /** Human-readable load failure. Shown above any options with a retry action. */
  error?: string | null;
  onRetry?: () => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * A searchable single-select combobox. The trigger is a button so a wrapping
 * `<label>` works normally; opening moves focus to a search field inside the
 * panel, where arrow keys move the active option, Enter chooses it and Escape
 * returns focus to the trigger. Loading, failed and empty result states are
 * distinct and announced. An optional `group` on an option renders a heading.
 */
export function SearchSelect({
  "aria-label": ariaLabel,
  id,
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No matches",
  loading = false,
  error = null,
  onRetry,
  disabled = false,
  required = false,
  className,
}: SearchSelectProps) {
  const baseId = useId();
  const listId = `${baseId}-listbox`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeValue, setActiveValue] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.keywords?.toLowerCase().includes(needle) ||
        option.group?.toLowerCase().includes(needle),
    );
  }, [options, query]);

  const groups = useMemo(() => {
    const out: Array<{ heading?: string; options: SearchSelectOption[] }> = [];
    for (const option of filtered) {
      const last = out[out.length - 1];
      if (last && last.heading === option.group) last.options.push(option);
      else out.push({ heading: option.group, options: [option] });
    }
    return out;
  }, [filtered]);

  const optionId = (option: SearchSelectOption) =>
    `${baseId}-option-${filtered.indexOf(option)}`;
  const activeOption = activeValue
    ? filtered.find((option) => option.value === activeValue)
    : undefined;

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const enabled = filtered.filter((option) => !option.disabled);
    if (enabled.some((option) => option.value === activeValue)) return;
    const preferred =
      enabled.find((option) => option.value === value) ?? enabled[0];
    setActiveValue(preferred?.value ?? null);
  }, [open, filtered, activeValue, value]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const openPanel = () => {
    if (disabled) return;
    setQuery("");
    setOpen(true);
  };

  const closeToTrigger = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const choose = (nextValue: string) => {
    onChange(nextValue);
    closeToTrigger();
  };

  const moveActive = (delta: number | "first" | "last") => {
    const enabled = filtered.filter((option) => !option.disabled);
    if (enabled.length === 0) return;
    const index = enabled.findIndex((option) => option.value === activeValue);
    const next =
      delta === "first"
        ? enabled[0]
        : delta === "last"
          ? enabled[enabled.length - 1]
          : enabled[
              Math.min(Math.max(index + delta, 0), enabled.length - 1)
            ] ?? enabled[0];
    setActiveValue(next.value);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === "Home") {
      event.preventDefault();
      moveActive("first");
    } else if (event.key === "End") {
      event.preventDefault();
      moveActive("last");
    } else if (event.key === "Enter") {
      event.preventDefault();
      const active = filtered.find(
        (option) => option.value === activeValue && !option.disabled,
      );
      if (active) choose(active.value);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeToTrigger();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={cn("search-select", className)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className="select search-select__trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required || undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            openPanel();
          }
        }}
      >
        <span
          className={cn(
            "search-select__value",
            !selected && "search-select__value--placeholder",
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className="search-select__chevron" aria-hidden="true" />
      </button>
      {open ? (
        <div
          className="search-select__panel"
          onClick={(event) => {
            // The picker is often wrapped in a <label>. Without this, a click
            // on a non-interactive option would activate the label, forward a
            // synthetic click to the trigger and immediately reopen the panel.
            event.preventDefault();
          }}
        >
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            className="input search-select__search"
            aria-label={searchPlaceholder}
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              activeOption ? optionId(activeOption) : undefined
            }
            placeholder={searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
          />
          {error ? (
            <div className="search-select__status" role="alert">
              <span>{error}</span>
              {onRetry ? (
                <button
                  type="button"
                  className="search-select__retry"
                  onClick={onRetry}
                >
                  <RotateCw className="size-3.5" aria-hidden="true" />
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}
          {loading ? (
            <div className="search-select__status" role="status">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Loading…
            </div>
          ) : (
            <div
              role="listbox"
              id={listId}
              aria-label={ariaLabel ?? searchPlaceholder}
              className="search-select__list"
            >
              {filtered.length === 0 ? (
                <p className="search-select__status" role="status">
                  {emptyMessage}
                </p>
              ) : (
                groups.map((group, groupIndex) =>
                  group.heading ? (
                    <div
                      role="group"
                      aria-label={group.heading}
                      key={`${group.heading}-${groupIndex}`}
                    >
                      <p className="search-select__group" aria-hidden="true">
                        {group.heading}
                      </p>
                      {group.options.map(renderOption)}
                    </div>
                  ) : (
                    group.options.map(renderOption)
                  ),
                )
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );

  function renderOption(option: SearchSelectOption) {
    const isSelected = option.value === value;
    const isActive = option.value === activeValue;
    return (
      <div
        key={option.value}
        id={optionId(option)}
        role="option"
        aria-selected={isSelected}
        aria-disabled={option.disabled || undefined}
        data-active={isActive || undefined}
        className={cn(
          "search-select__option",
          isActive && "search-select__option--active",
        )}
        onMouseEnter={() => {
          if (!option.disabled) setActiveValue(option.value);
        }}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!option.disabled) choose(option.value);
        }}
      >
        <span>{option.label}</span>
        {isSelected ? (
          <span className="search-select__check" aria-hidden="true">
            ✓
          </span>
        ) : null}
      </div>
    );
  }
}
